import { useState } from 'react'
import * as RadixPopover from '@radix-ui/react-popover'
import { useNavigate } from 'react-router-dom'
import { Button, Icon } from '@navegasistemas/dls-navega-rjs'
import { useExports } from '@/features/file-exports/hooks/use-exports'
import { useDownloadExport } from '@/features/file-exports/hooks/use-download-export'
import { ExportStatusBadge } from '@/features/file-exports/components/export-status-badge'
import type { ExportJob } from '@/features/file-exports/types/export-job.types'

const FORMAT_LABELS: Record<string, string> = {
  csv: 'CSV',
  xlsx: 'Excel',
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface ExportPopoverItemProps {
  job: ExportJob
  onDownload: (id: string) => void
  isDownloading: boolean
}

function ExportPopoverItem({ job, onDownload, isDownloading }: ExportPopoverItemProps) {
  const formatLabel = FORMAT_LABELS[job.format] ?? job.format.toUpperCase()

  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-sm font-medium text-[var(--color-text-primary)]">
            {job.label}
          </span>
          <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">{formatLabel}</span>
          <ExportStatusBadge status={job.status} />
        </div>
        <span className="text-xs text-[var(--color-text-secondary)]">
          {formatDate(job.createdAt)}
        </span>

        {job.status === 'processing' && job.progress != null && (
          <div className="flex items-center gap-2">
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-[var(--color-neutral-200)]">
              <div
                className="h-full rounded-full bg-[var(--color-primary-500)] transition-all"
                style={{ width: `${job.progress}%` }}
              />
            </div>
            <span className="text-xs tabular-nums text-[var(--color-text-secondary)]">
              {job.progress}%
            </span>
          </div>
        )}
      </div>

      {(job.status === 'pending' || job.status === 'processing') && (
        <svg
          className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[var(--color-primary-500)]"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      )}

      {job.status === 'completed' && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onDownload(job.id)}
          isLoading={isDownloading}
          aria-label="Baixar arquivo"
        >
          <Icon name="download" size="sm" />
        </Button>
      )}
    </div>
  )
}

export function ExportsPopover() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const { data } = useExports(1)
  const { download, downloadingId } = useDownloadExport()

  const jobs = (data?.data ?? []).slice(0, 6)

  // Track which completed job IDs were already seen when the component initialized.
  // Any completed job that appears after initialization is "unread".
  const [seenIds, setSeenIds] = useState<Set<string> | null>(null)
  if (seenIds === null && data) {
    setSeenIds(new Set(data.data.filter((j) => j.status === 'completed').map((j) => j.id)))
  }

  const hasActive = jobs.some((j) => j.status === 'pending' || j.status === 'processing')
  const hasUnread = !hasActive && jobs.some((j) => j.status === 'completed' && !seenIds?.has(j.id))

  function handleOpenChange(next: boolean) {
    if (next) {
      setSeenIds((prev) => {
        const updated = new Set(prev ?? [])
        jobs.filter((j) => j.status === 'completed').forEach((j) => updated.add(j.id))
        return updated
      })
    }
    setOpen(next)
  }

  return (
    <RadixPopover.Root open={open} onOpenChange={handleOpenChange}>
      <RadixPopover.Trigger asChild>
        <button
          className="relative flex h-8 w-8 items-center justify-center rounded-lg text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-app)] hover:text-[var(--color-text-primary)]"
          aria-label="Exportações"
        >
          <Icon name="download" size="md" />
          {hasActive && (
            <span className="absolute right-1 top-1 h-2 w-2 animate-pulse rounded-full bg-[var(--color-warning-500)]" />
          )}
          {hasUnread && (
            <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-[var(--color-success-500)]" />
          )}
        </button>
      </RadixPopover.Trigger>

      <RadixPopover.Portal>
        <RadixPopover.Content
          align="end"
          sideOffset={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-50 w-80 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-lg outline-none"
        >
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
            <span className="text-sm font-semibold text-[var(--color-text-primary)]">
              Exportações
            </span>
            <Button
              variant="ghost"
              onClick={() => {
                navigate('/exportacoes')
                setOpen(false)
              }}
            >
              Ver todas
            </Button>
          </div>

          {jobs.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-[var(--color-text-secondary)]">
              Nenhuma exportação encontrada.
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-[var(--color-border)]">
              {jobs.map((job) => (
                <ExportPopoverItem
                  key={job.id}
                  job={job}
                  onDownload={download}
                  isDownloading={downloadingId === job.id}
                />
              ))}
            </div>
          )}
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  )
}
